import { useLayoutEffect, useState } from 'react';
import { MonthRefs } from './types';

const createVisibilityMap = (monthRefs: MonthRefs) => {
  const visibilityMap = new Map<Element, boolean>();
  for (const monthRef of Object.values(monthRefs)) {
    if (monthRef.current) visibilityMap.set(monthRef.current, false);
  }
  return visibilityMap;
};

export const useVisibilityMap = (viewport: HTMLDivElement | null, monthRefs: MonthRefs) => {
  // As each observable child attaches itself to the parent observer, we need to
  // map Elements to Callbacks so that when an Element's intersection changes,
  // we'll know which callback to invoke. For this, we'll use an ES6 Map.
  const [visibilityMap, setVisibilityMap] = useState<Map<Element, boolean>>(new Map());

  useLayoutEffect(() => {
    if (!viewport) return;
    // initialize visibility map
    setVisibilityMap(createVisibilityMap(monthRefs));
    const intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // update the visibility of the elements as they intersect with the viewport
          setVisibilityMap(
            visibilityMap => new Map(visibilityMap.set(entry.target, entry.isIntersecting)),
          );
        });
      },
      {
        root: viewport,
        rootMargin: '100px',
      },
    );
    // observe all months with a single Intersection Observer
    for (const monthRef of Object.values(monthRefs)) {
      if (monthRef.current) intersectionObserver.observe(monthRef.current);
    }
    return () => {
      // cleanup
      setVisibilityMap(new Map());
      intersectionObserver.disconnect();
    };
  }, [viewport, monthRefs]);

  return visibilityMap;
};
